import API from "../../helpers/network/http";
import {
    ProfessionalPortfolio,
    ProfessionalPortfolioItem,
    ProfessionalProfile,
    ProfessionalProfileRecord,
    ProfessionalRatingSummary,
    ProfessionalSkill,
    UserPayoutAccount
} from "../../utils/types";
import {
    UpdatePortfolioPayload,
    UpdateProfessionalProfilePayload,
    UpsertPortfolioItemPayload,
    UpsertPayoutAccountPayload,
    UpsertSkillPayload
} from "./types";

export type PayoutBankOption = {
    name: string;
    code: string;
};

export default class ProfileService {
    static async getUserContactByUsername(username: string): Promise<{email?: string; username?: string; first_name?: string; last_name?: string}> {
        const {data} = await API.get(`/users/username/${username}`);
        return data.data || {};
    }

    static async getMyProfile(): Promise<ProfessionalProfile> {
        const {data} = await API.get('/profiles/me');
        return data.data;
    }

    static async initializeMyProfile(): Promise<{initialized: boolean}> {
        const {data} = await API.post('/profiles/me/initialize');
        return data.data;
    }

    static async getPublicProfile(username: string): Promise<ProfessionalProfile> {
        const {data} = await API.get(`/profiles/${username}`);
        return data.data;
    }

    static async getPortfolioItems(username: string): Promise<ProfessionalPortfolioItem[]> {
        const {data} = await API.get(`/profiles/${username}/portfolio-items`);
        return data.data || [];
    }

    static async getSkills(username: string): Promise<ProfessionalSkill[]> {
        const {data} = await API.get(`/profiles/${username}/skills`);
        return data.data || [];
    }

    static async getRatingSummary(username: string): Promise<ProfessionalRatingSummary | null> {
        const {data} = await API.get(`/profiles/${username}/rating-summary`);
        return data.data || null;
    }

    static async updateMyProfile(payload: UpdateProfessionalProfilePayload): Promise<ProfessionalProfileRecord> {
        const {data} = await API.put('/profiles/me', payload);
        return data.data;
    }

    static async updateMyPortfolio(payload: UpdatePortfolioPayload): Promise<ProfessionalPortfolio> {
        const {data} = await API.put('/profiles/me/portfolio', payload);
        return data.data;
    }

    static async createPortfolioItem(payload: UpsertPortfolioItemPayload): Promise<ProfessionalPortfolioItem> {
        const {data} = await API.post('/profiles/me/portfolio-items', payload);
        return data.data;
    }

    static async updatePortfolioItem(itemId: string | number, payload: UpsertPortfolioItemPayload): Promise<ProfessionalPortfolioItem> {
        const {data} = await API.put(`/profiles/me/portfolio-items/${itemId}`, payload);
        return data.data;
    }

    static async deletePortfolioItem(itemId: string | number): Promise<void> {
        await API.delete(`/profiles/me/portfolio-items/${itemId}`);
    }

    static async createSkill(payload: UpsertSkillPayload): Promise<ProfessionalSkill> {
        const {data} = await API.post('/profiles/me/skills', payload);
        return data.data;
    }

    static async updateSkill(skillId: string | number, payload: UpsertSkillPayload): Promise<ProfessionalSkill> {
        const {data} = await API.put(`/profiles/me/skills/${skillId}`, payload);
        return data.data;
    }

    static async deleteSkill(skillId: string | number): Promise<void> {
        await API.delete(`/profiles/me/skills/${skillId}`);
    }

    static async getMyPayoutAccounts(provider?: 'paystack' | 'stripe'): Promise<UserPayoutAccount[]> {
        const {data} = await API.get('/profiles/me/payout-accounts', {
            params: provider ? {provider} : undefined,
        });
        return data.data || [];
    }

    static async createMyPayoutAccount(payload: UpsertPayoutAccountPayload): Promise<UserPayoutAccount> {
        const {data} = await API.post('/profiles/me/payout-accounts', payload);
        return data.data;
    }

    static async updateMyPayoutAccount(accountId: string | number, payload: UpsertPayoutAccountPayload): Promise<UserPayoutAccount> {
        const {data} = await API.put(`/profiles/me/payout-accounts/${accountId}`, payload);
        return data.data;
    }

    static async deleteMyPayoutAccount(accountId: string | number): Promise<void> {
        await API.delete(`/profiles/me/payout-accounts/${accountId}`);
    }

    static async getMyPayoutBanks(country: string): Promise<PayoutBankOption[]> {
        const {data} = await API.get('/profiles/me/payout-accounts/banks', {
            params: {country},
        });
        return data.data || [];
    }
}
